import {
  CourseStateType,
  EnrolState,
  NotificationReminder,
  SynchronousCourseTypes,
} from 'enum'
import { EmailTypes } from '../enum/emailTypes.enum'
import { CourseType } from '../enum/course-type.enum'
import { MonitoringStatus } from '../enum/monitoring-status.enum'
import { ILmsProfileModel } from './lmsProfile.model'
import { ILessonModel } from './lesson.model'
import {
  IEnrolModel,
  enrolInitialState,
  SynchronousCourseProperties,
} from './enrol.model'

import { ISenceIntegrationForm, ISenceIntegration } from './integrations'
import { IEnrolCard } from './enrolCard.model'
import { IExternalIntegrationForm } from './integrations/external'

export interface Notification {
  emailSubject: string
  emailBody: string
  emailType: EmailTypes
  active: boolean
  config?: NotificationConfig
  sent?: boolean
}

export interface ReminderNotification {
  emailBody: string
  active: boolean
  config?: NotificationConfig
}

export interface NotificationConfig {
  /**
   * @description Time in minutes
   */
  delayTime: NotificationReminder
}

interface CoursesLmsProfile {
  id: number
  kind: number
  courseId: number
  lmsProfileId: number
  createdAt: string
  updatedAt: string
}

export interface SynchronousCourse extends SynchronousCourseProperties {
  id?: number
  synchronousCourseType: SynchronousCourseTypes
  synchronousCourseSource: {
    value: string
  }
}

export interface ICourseModelBase {
  id: number
  capacity?: number
  description: string
  name: string
  order?: number
  startDate?: string | Date
  state: CourseStateType
  courseType: CourseType
  courseTypeHumanize: string
  untilDate?: string | Date
  optionalProfiles?: ILmsProfileModel[]
  requiredProfiles?: ILmsProfileModel[]
  coursesLmsProfilesAttributes?: any[]
  coursesLmsProfiles?: CoursesLmsProfile[]
  createdAt: string
  updatedAt?: string
  banner?: File
  thumbnail?: File
  bannerUrl?: string
  thumbnailUrl?: string
  lessons: ILessonModel[]
  enrol?: IEnrolModel
  advance: MonitoringStatus
  directChiefApproval?: boolean
  mandatory?: boolean
  onDuplicated?: boolean
  stateAdmin: any
  lessonContent?: any
  hasResponses?: boolean
  canGenerateCertificate?: boolean
  invitationNotification?: Notification | null
  reminderNotification?: ReminderNotification | null
  reminderNoStartCourseNotification?: Notification
  synchronousCourse?: SynchronousCourse
  synchronousEnrolFinalState: EnrolState
  bannerData?: any
  thumbnailData?: any
  individualCertificatesName?: string
  grupalCertificatesCount?: number
  hours?: number | null
  tasksCount?: number
  surveysCount?: number
  evaluationsCount?: number
  approved?: boolean
  canRestart?: boolean
  finished: number
  integrationCourseId?: number
  integrationCourseLink?: string
}

export interface SynchronousCourseDestroy {
  _destroy: boolean
}

type SynchronousCourseParam = SynchronousCourseDestroy | SynchronousCourse

export type ICourseModel = ICourseModelBase & {
  senceIntegration?: ISenceIntegration
  assigned: boolean
}

export type ICourseGetModel = ICourseModel & {
  cardEnrolData: IEnrolCard | null
}

export interface ICourseModelUpdate
  extends Omit<ICourseModelBase, 'synchronousCourse' | 'id'> {
  synchronousCourse?: SynchronousCourseParam
  senceIntegration?: ISenceIntegrationForm
}

export const courseInitialState: Omit<ICourseModel, 'id'> = {
  name: '',
  description: '',
  courseType: CourseType.Open,
  state: CourseStateType.Draft,
  courseTypeHumanize: '',
  optionalProfiles: [],
  requiredProfiles: [],
  bannerUrl: '',
  thumbnailUrl: '',
  lessons: [],
  enrol: enrolInitialState,
  advance: 0,
  directChiefApproval: false,
  createdAt: '',
  stateAdmin: null,
  invitationNotification: null,
  canGenerateCertificate: false,
  finished: 0,
  synchronousEnrolFinalState: EnrolState.Pending,
  assigned: false,
}

interface CourseParams {
  name: string
  description: string
  courseType: number
  state?: CourseStateType
  startDate?: string
  untilDate?: string
  banner?: File
  thumbnail?: File
  coursesLmsProfilesAttributes?: any[]
  directChiefApproval?: boolean
  invitationNotification?: Notification | null
  canGenerateCertificate?: boolean
  reminderNotification?: ReminderNotification | null
  reminderNoStartCourseNotification?: Notification
  synchronousCourse?: SynchronousCourseParam
  hours?: number | string
  canRestart?: boolean
  integrations?: {
    sence?: ISenceIntegrationForm
    // Used for external integrations like Coursera, Udemy, etc.
    external?: IExternalIntegrationForm
  }
}

export const NewCourse = ({
  name,
  description,
  courseType,
  state = CourseStateType.Draft,
  startDate,
  untilDate,
  banner,
  thumbnail,
  coursesLmsProfilesAttributes,
  directChiefApproval,
  invitationNotification,
  canGenerateCertificate,
  reminderNotification,
  reminderNoStartCourseNotification,
  synchronousCourse,
  hours,
  canRestart,
  integrations,
}: CourseParams): ICourseModelUpdate => {
  const course: ICourseModelUpdate = courseInitialState

  course.name = name
  course.description = description
  course.state = state
  course.courseType = courseType
  course.startDate = startDate
  course.untilDate = untilDate
  if (banner) course.banner = banner
  if (thumbnail) course.thumbnail = thumbnail
  if (coursesLmsProfilesAttributes)
    course.coursesLmsProfilesAttributes = coursesLmsProfilesAttributes
  course.directChiefApproval = directChiefApproval
  course.invitationNotification = !invitationNotification
    ? undefined
    : invitationNotification //Maybe not
  course.canGenerateCertificate = canGenerateCertificate
  course.reminderNotification = reminderNotification
  course.reminderNoStartCourseNotification = reminderNoStartCourseNotification
  course.synchronousCourse = synchronousCourse
  course.hours = +(hours || 0)
  course.canRestart = canRestart
  course.senceIntegration = integrations?.sence

  const external = integrations?.external
  if (external) {
    course['integration_course_id'] = external.id
    course['integration_course_link'] = external.link
  }

  return course
}

export interface ICourseNameList {
  name: string
  id: number
  profiles: any[]
}
